/**
 * Lab fulfillment journey shapes.
 * Mirrors backend/src/modules/lab-fulfillment/lab-fulfillment.validators.ts.
 */
import type { LabSampleType } from '@/features/lab/types';
import type { CatalogImage } from '@/features/catalog-image/types';

export type LabFulfillmentStatus = 'CONFIRMED' | 'SAMPLE_COLLECTED' | 'PROCESSING' | 'REPORT_READY';

export type LabFulfillment = {
  booking_id: string;
  status: LabFulfillmentStatus;
  test_name: string | null;
  sample_type: LabSampleType | null;
  turnaround_hours: number | null;
  provider_name: string | null;
  confirmed_at: string | null;
  sample_collected_at: string | null;
  processing_at: string | null;
  report_ready_at: string | null;
  note: string | null;
  /** Report notes — present only once REPORT_READY. */
  report_notes: string | null;
  reported_at: string | null;
  /** Provider-attached report doc(s) (≤5) — present only once REPORT_READY. */
  report_documents: CatalogImage[];
};

/** Forward-only journey order (mirrors the backend STAGE_ORDER). */
export const LAB_STAGE_ORDER: LabFulfillmentStatus[] = [
  'CONFIRMED',
  'SAMPLE_COLLECTED',
  'PROCESSING',
  'REPORT_READY',
];

const LAB_STAGE_LABEL: Record<LabFulfillmentStatus, string> = {
  CONFIRMED: 'Confirmed',
  SAMPLE_COLLECTED: 'Sample collected',
  PROCESSING: 'Processing',
  REPORT_READY: 'Report ready',
};

export function stage_label(status: LabFulfillmentStatus): string {
  return LAB_STAGE_LABEL[status];
}

/** The timestamp field each stage stamps, for rendering a dated timeline. */
export const LAB_STAGE_TIMESTAMP: Record<LabFulfillmentStatus, keyof LabFulfillment> = {
  CONFIRMED: 'confirmed_at',
  SAMPLE_COLLECTED: 'sample_collected_at',
  PROCESSING: 'processing_at',
  REPORT_READY: 'report_ready_at',
};

/** The verb a provider taps to move into each stage (CONFIRMED has no advance). */
const LAB_ADVANCE_LABEL: Partial<Record<LabFulfillmentStatus, string>> = {
  SAMPLE_COLLECTED: 'Mark sample collected',
  PROCESSING: 'Begin processing',
  REPORT_READY: 'Mark report ready',
};

export function advance_label(status: LabFulfillmentStatus): string {
  return LAB_ADVANCE_LABEL[status] ?? 'Advance';
}

export type AdvanceLabFulfillmentInput = {
  to: LabFulfillmentStatus;
  note?: string;
  report_notes?: string;
};
